import type {
  CommentResponseDto,
  ListCommentsResponseDto,
} from '@inference.engine/workspace-api-client/web';
import type { InfiniteData } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '@/lib/services/notification.service';
import { logger } from '@/lib/utils/logger';
import type { CommentTargetRef } from '@/entities/project-comment';
import { isCommentsListKey, PROJECT_COMMENT_QUERY_KEYS } from '@/entities/project-comment';
import { invalidateCommentAnnotations } from './invalidate-comment-annotations';
import { updateCommentStatus } from './comments.api';
import { useTranslation } from 'react-i18next';

export { updateCommentStatus };

interface UseResolveCommentOptions {
  projectId: string;
  target: CommentTargetRef;
}

interface ResolveCommentArgs {
  commentId: string;
  status: 'pending' | 'resolved';
  suggestionStatus?: 'applied' | 'ignored';
}

type RootInfiniteData = InfiniteData<ListCommentsResponseDto, number>;

function patchStatusInRoots(commentId: string, status: 'pending' | 'resolved') {
  return (old: RootInfiniteData | undefined): RootInfiniteData | undefined => {
    if (!old) return old;
    const pages = old.pages.map((page) => {
      const index = page.items.findIndex((item) => item.id === commentId);
      if (index === -1) return page;
      const items = [...page.items];
      // Patch only status — resolvedBy/resolvedAt come back from the server on
      // invalidate. Trying to mint them client-side requires the caller's id
      // here and risks drift from the server clock.
      const next: CommentResponseDto = { ...items[index], status };
      items[index] = next;
      return { ...page, items };
    });
    // No page produced a new ref → comment wasn't in this cache variant; bail.
    if (pages.every((p, i) => p === old.pages[i])) return old;
    return { ...old, pages };
  };
}

export function useResolveComment(options: UseResolveCommentOptions) {
  const { t } = useTranslation('projects');

  const queryClient = useQueryClient();
  const { projectId, target } = options;
  const rootKey = PROJECT_COMMENT_QUERY_KEYS.list(projectId, target.targetType, target.targetId);

  // `rootKey` is a prefix that also matches the `[…, 'comments', 'replies',
  // parentId]` cache, whose value shape is a flat `ListCommentsResponseDto`
  // (not `InfiniteData`). Without this predicate, every cache op below would
  // run against the replies cache and the patcher would crash on
  // `old.pages.map` whenever a reply thread was open.
  const rootListFilter = {
    queryKey: rootKey,
    predicate: (q: { queryKey: readonly unknown[] }) => isCommentsListKey(q.queryKey),
  };

  return useMutation({
    mutationFn: ({ commentId, status, suggestionStatus }: ResolveCommentArgs) =>
      updateCommentStatus(projectId, target, commentId, {
        status,
        ...(suggestionStatus ? { suggestionStatus } : {}),
      }),
    onMutate: async ({ commentId, status }) => {
      // Cancel in-flight refetches so they don't overwrite the optimistic patch
      // before the server responds.
      await queryClient.cancelQueries(rootListFilter);
      const previous = queryClient.getQueriesData<RootInfiniteData>(rootListFilter);
      queryClient.setQueriesData<RootInfiniteData>(
        rootListFilter,
        patchStatusInRoots(commentId, status),
      );
      return { previous };
    },
    onError: (error, _vars, context) => {
      logger.error('Failed to update comment status:', error);
      notificationService.showError(
        t('commentOnFile.useResolveComment.failedToUpdateCommentStatus'),
        {
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        },
      );
      // Roll back every variant we patched.
      context?.previous.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    // Refetch all sorted / showResolved variants so the resolved item flips
    // visibility correctly (e.g. disappears when the sidebar is hiding resolved
    // comments) and server-stamped resolvedBy/resolvedAt land in cache.
    onSettled: () => {
      void queryClient.invalidateQueries(rootListFilter);
      invalidateCommentAnnotations(queryClient, projectId, target);
    },
  });
}
